import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { circulationApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Loan, LoanStatus } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { LoanStatusBadge, overdueText } from './shared';

const STATUSES: { value: LoanStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Out on loan' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'LOST', label: 'Lost' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/**
 * Every loan the school has made.
 *
 * <p>This is the history, not the desk: "what is out right now" is a filter over
 * it rather than a separate idea. A closed loan is never deleted — a member's
 * borrowing record is the thing a librarian is asked about most often, and it
 * survives the title being withdrawn from the catalogue.
 */
export function LoansPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('issueDate,desc');
  const renews = can(PERMISSIONS.CIRCULATION_RENEW);

  const [status, setStatus] = useState<LoanStatus | ''>('');

  const params = { ...table.params, ...(status === '' ? {} : { status: [status] }) };

  const loans = useQuery({
    queryKey: queryKeys.circulation.loans(params),
    queryFn: () => circulationApi.loans(params),
  });

  const renew = useMutation({
    mutationFn: (id: string) => circulationApi.renew(id),
    onSuccess: (loan) => {
      toast.success(`${loan.bookTitle} — now due ${formatDate(loan.dueDate)}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.circulation.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Loan>[] = [
    {
      key: 'transactionNumber',
      header: 'Loan',
      render: (row) => (
        <span>
          <span className="block font-mono text-[12px] text-ink">{row.transactionNumber}</span>
          <span className="block text-[11px] text-subtle">{formatDate(row.issueDate)}</span>
        </span>
      ),
    },
    {
      key: 'book',
      header: 'Book',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.bookTitle}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.accessionNumber}</span>
        </span>
      ),
    },
    {
      key: 'member',
      header: 'Borrower',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.memberName}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.membershipNumber}</span>
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due',
      sortKey: 'dueDate',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{formatDate(row.dueDate)}</span>
          {row.overdue && (
            <span className="block text-[11px] text-[color:var(--sms-danger)]">
              {overdueText(row.daysOverdue)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'returnDate',
      header: 'Returned',
      render: (row) => orDash(row.returnDate === undefined ? undefined : formatDate(row.returnDate)),
    },
    {
      key: 'renewals',
      header: 'Renewals',
      className: 'text-right',
      render: (row) => `${row.renewalCount} of ${row.maxRenewals}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <LoanStatusBadge status={row.status} overdue={row.overdue} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        !renews || row.status !== 'ACTIVE' ? null : row.renewable ? (
          <Button size="sm" variant="secondary" onClick={() => renew.mutate(row.id)}>
            Renew
          </Button>
        ) : (
          // The server's reason, not ours. Restating it here in different words
          // is how the two come to disagree.
          <span className="text-[11px] text-muted" title={row.renewalBlockers.join('; ')}>
            {row.renewalBlockers[0] ?? '—'}
          </span>
        ),
    },
  ];

  return (
    <ListPage
      title="Loans"
      description="Every book that has gone out, and whether it came back."
      icon={<BookOpen className="h-5 w-5" aria-hidden="true" />}
      filters={
        <FilterSelect
          label="Status"
          value={status}
          onChange={(value) => {
            setStatus(value);
            table.setPage(0);
          }}
          options={STATUSES}
        />
      }
      pagination={loans.data?.pagination}
      onPageChange={table.setPage}
      onSizeChange={table.setSize}
    >
      <DataTable
        columns={columns}
        rows={loans.data?.data}
        rowKey={(row) => row.id}
        loading={loans.isPending}
        error={loans.error}
        onRetry={() => void loans.refetch()}
        sort={table.sort}
        onSort={table.toggleSort}
        emptyTitle="No loans"
        emptyDescription="Nothing has been borrowed yet."
      />
    </ListPage>
  );
}
